import React, { useEffect, useState } from 'react';
import { Platform, ScrollView, View, useWindowDimensions } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import { useT } from '@transifex/react';

import { getEmailFromAccessToken } from '../biometric/accessTokenClaims';
import { useBiometricEnrollmentPrompt } from '../biometric/BiometricEnrollmentProvider';
import { AuthFooter } from '../components/AuthFooter';
import { AuthLayout } from '../components/AuthLayout';
import { BaseButton } from '../components/BaseButton';
import { BloomButton } from '../components/BloomButton';
import { InlineError } from '../components/InlineError';
import { Input } from '../components/Input';
import Loading from '../components/Loading';
import { Logo } from '../components/Logo';
import { TextLink } from '../components/TextLink';
import ThemedText from '../components/ThemedText';
import { useThemePalette } from '../contexts/ThemePalette';
import { ROUTES, EXTERNAL_URLS, ERROR_MESSAGES } from '../utils/constants';
import { screenTestId } from '../utils/testId';
import { sanitizeRedirectUrl } from '../utils/redirect';
import {
  fetchOrganizations,
  generateSamlUrl,
  getSpecialSSOUrl,
  getZendeskSSOUrl,
} from '../api/sso';
import { completeAuth, handleSsoCallback } from '../services/authService';
import { Organization } from '../types';
import { analytics } from '../utils/analytics';
import { useLoginRouter } from '../hooks/useLoginRouter';
import { useOnAuthenticated } from '../hooks/useOnAuthenticated';

const tid = screenTestId('SsoLogin');

export default function SSOLoginScreen() {
  const router = useLoginRouter();
  const onAuthenticated = useOnAuthenticated();
  const { promptIfEligible } = useBiometricEnrollmentPrompt();
  const params = useLocalSearchParams();
  const { width } = useWindowDimensions();
  const palette = useThemePalette();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [suggestions, setSuggestions] = useState<Organization[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  // Bridge state: true from the moment the OS browser closes (success)
  // until completeAuth navigates. Without this the sso-login screen sits
  // static for the ~500-800ms token-exchange window and reads as
  // unresponsive on native.
  const [finalizing, setFinalizing] = useState(false);

  const nextUrl = typeof params.next === 'string' ? params.next : undefined;
  const returnTo = typeof params.return_to === 'string' ? params.return_to : undefined;
  const zendeskSsoUrl = getZendeskSSOUrl(returnTo);
  const isDesktop = width >= 768;
  const t = useT();
  const [autoRedirecting, setAutoRedirecting] = useState(false);

  // Auto-redirect for coaches/auditors/health guides — if ?next= matches
  // a known app path, skip org selection and go straight to Okta SAML.
  useEffect(() => {
    if (Platform.OS !== 'web' || !nextUrl) return;
    const safeNext = sanitizeRedirectUrl(nextUrl, '/');
    const specialUrl = getSpecialSSOUrl(safeNext);
    if (specialUrl) {
      setAutoRedirecting(true);
      analytics.trackEvent('sso_auto_redirect', { next: safeNext });
      window.location.href = generateSamlUrl(specialUrl, safeNext);
    }
  }, [nextUrl]);

  useEffect(() => {
    const loadOrganizations = async () => {
      setOrganizationsLoading(true);
      try {
        const orgs = await fetchOrganizations();
        setOrganizations(orgs);
      } catch (err) {
        setError(t('Failed to load organizations'));
      } finally {
        setOrganizationsLoading(false);
      }
    };

    loadOrganizations();
  }, []);

  const handleOrganizationSearch = (text: string) => {
    setSearchTerm(text);
    setSelectedOrg(null);

    if (text.trim() === '') {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    if (organizations.length > 0) {
      const filtered = organizations.filter((org) =>
        org.name.toLowerCase().includes(text.toLowerCase())
      );
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectOrganization = (org: Organization) => {
    setSearchTerm(org.name);
    setSelectedOrg(org);
    setShowSuggestions(false);
    setError(null);
    analytics.trackEvent('sso_org_selected', { org_name: org.name });
  };

  const handleSubmit = async () => {
    setError(null);

    if (!selectedOrg) {
      setError(t('Please select an organization'));
      return;
    }

    if (!selectedOrg.sso_url) {
      setError(ERROR_MESSAGES.SSO_NOT_SUPPORTED);
      return;
    }

    setLoading(true);
    analytics.trackEvent('sso_attempted', { org_name: selectedOrg.name });
    // On success we intentionally leave `finalizing` true: the consumer's
    // onAuthenticated (VRN) queues a router.replace('/'), which resolves
    // one frame later. If we cleared here, sso-login re-renders as the
    // plain form for that frame, and on Android — where the transition
    // is slower than iOS' native animation — /home paints unauthed
    // before AppContext.isAuthenticated flips. Let the /login sub-tree
    // unmount clear the state naturally.
    let succeeded = false;

    try {
      const safeNextUrl = sanitizeRedirectUrl(nextUrl, '/');
      const specialUrl = getSpecialSSOUrl(safeNextUrl);
      const baseSsoUrl = specialUrl ?? selectedOrg.sso_url;

      if (Platform.OS === 'web') {
        const ssoUrl = generateSamlUrl(baseSsoUrl, safeNextUrl);
        analytics.trackEvent('sso_redirected', { org_name: selectedOrg.name });
        window.location.href = ssoUrl;
        return;
      }

      // Native: open the SAML flow in a system-managed browser
      // (ASWebAuthenticationSession on iOS, Chrome Custom Tabs on Android)
      // with our app scheme as the redirect target. The SSO service, when
      // it sees a whitelisted app scheme in RelayState, 302s the browser
      // straight to `vidahealthsso://sso-callback?code=<x>` after SAML —
      // the login-web SPA is skipped entirely. The scheme match closes
      // the AuthTab and hands the URL back to us; we redeem the one-shot
      // code for tokens in-app via /v1/auth/sso-exchange. Tokens never
      // travel through the URL.
      const nativeReturnUrl = 'vidahealthsso://sso-callback';
      const nativeSamlUrl = generateSamlUrl(baseSsoUrl, nativeReturnUrl);

      // Set finalizing BEFORE opening the browser so that on Android,
      // when Chrome Custom Tabs closes and the Activity resumes, the
      // task-switch animation paints the "Signing you in..." overlay
      // (already rendered) rather than the sso-login form. iOS's
      // ASWebAuthenticationSession is not activity-based so the paint
      // race doesn't exist there, but setting early is harmless.
      setFinalizing(true);
      const result = await WebBrowser.openAuthSessionAsync(nativeSamlUrl, nativeReturnUrl);
      if (result.type === 'success' && result.url) {
        const returned = new URL(result.url);
        const code = returned.searchParams.get('code');
        if (!code) {
          analytics.trackEvent('sso_failed', { org_name: selectedOrg.name });
          setError(t('Sign-in failed. Please try again.'));
          return;
        }
        analytics.trackEvent('sso_redirected', { org_name: selectedOrg.name });
        const { token, nextApp } = await handleSsoCallback(code);
        analytics.trackEvent('sso_callback_succeeded');
        await promptIfEligible({
          accessToken: token,
          email: getEmailFromAccessToken(token) ?? '',
        });
        await completeAuth({ token, nextApp, onAuthenticated });
        succeeded = true;
      } else if (result.type === 'cancel' || result.type === 'dismiss') {
        analytics.trackEvent('sso_cancelled', { org_name: selectedOrg.name });
      } else {
        analytics.trackEvent('sso_failed', { org_name: selectedOrg.name });
        setError(t('Sign-in failed. Please try again.'));
      }
    } catch (err: any) {
      analytics.trackEvent('sso_failed', { org_name: selectedOrg.name });
      setError(t('Failed to redirect to SSO provider. Please try again.'));
    } finally {
      setLoading(false);
      if (!succeeded) setFinalizing(false);
    }
  };

  const handleBackToLogin = () => {
    // Dropping return_to would cost a member the Zendesk handoff on re-login.
    const backParams: Record<string, string> = {};
    if (nextUrl) backParams.next = nextUrl;
    if (returnTo) backParams.return_to = returnTo;
    router.push(
      Object.keys(backParams).length ? { pathname: ROUTES.LOGIN, params: backParams } : ROUTES.LOGIN
    );
  };

  // RelayState is honored only when EXPO_PUBLIC_SSO_ZENDESK_URL is the app's
  // SAML endpoint; the tile URL prod ships today ignores it and lands on
  // Zendesk home.
  const handleZendeskOktaSignIn = () => {
    if (!zendeskSsoUrl) return;
    let target: string;
    try {
      target = generateSamlUrl(zendeskSsoUrl, returnTo);
    } catch {
      analytics.trackEvent('sso_zendesk_okta_failed');
      setError(t('Failed to redirect to SSO provider. Please try again.'));
      return;
    }
    // No properties: return_to is necessarily set here, and is a ticket URL.
    analytics.trackEvent('sso_zendesk_okta_redirect');
    window.location.href = target;
  };

  // Dropdown shadow/positioning must stay as style props (dynamic layout)
  const dropdownStyle = {
    position: 'absolute' as const,
    top: 72,
    left: 0,
    right: 0,
    backgroundColor: palette.surface.pure,
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 200,
    boxShadow: '0px 4px 12px 0px rgba(0, 0, 0, 0.1)',
    zIndex: 1000,
    borderWidth: 1,
    borderColor: palette.gray[10],
  };

  if (autoRedirecting) {
    return (
      <AuthLayout>
        <View className='w-full'>
          <Logo />
          <View className='items-center justify-center py-16'>
            <Loading className='flex-none' />
            <ThemedText type='bodyRegular' color='subtle' className='mt-4'>
              {t('Redirecting to your identity provider...')}
            </ThemedText>
          </View>
        </View>
      </AuthLayout>
    );
  }

  if (finalizing) {
    return (
      <AuthLayout>
        <View className='w-full'>
          <Logo />
          <View className='items-center justify-center py-16'>
            <Loading className='flex-none' />
            <ThemedText type='bodyRegular' color='subtle' className='mt-4'>
              {t('Signing you in...')}
            </ThemedText>
          </View>
        </View>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <View className='w-full'>
        <Logo />

        <View className='w-full'>
          <ThemedText type='titleHero' className='mb-3 font-bold' style={{ fontSize: 40, lineHeight: 48 }}>
            {t('Which organization are you eligible with?')}
          </ThemedText>

          <View className='flex-row items-center mb-8'>
            <ThemedText type='bodySmall' color='subtle'>
              {t('Need some help?')}{' '}
            </ThemedText>
            <TextLink href={EXTERNAL_URLS.SUPPORT} testID={tid('contact-us-link')}>{t('Contact us.')}</TextLink>
          </View>

          <InlineError message={error} testID={tid('error')} />

          <View style={{ position: 'relative', zIndex: 10 }}>
            <Input
              label={t('Organization')}
              value={searchTerm}
              onChangeText={handleOrganizationSearch}
              placeholder={t('Your organization')}
              maxLength={254}
              autoComplete='organization'
              autoCorrect={false}
              spellCheck={false}
              onFocus={() => searchTerm && setShowSuggestions(true)}
              loading={organizationsLoading}
              containerStyle={{ marginBottom: 0 }}
              testID={tid('organization-input')}
            />

            {showSuggestions && suggestions.length > 0 && (
              <View style={dropdownStyle}>
                <ScrollView
                  style={{ maxHeight: 200 }}
                  keyboardShouldPersistTaps='handled'
                  nestedScrollEnabled
                >
                  {suggestions.map((item, index) => (
                    <BaseButton
                      key={`${item.uuid}-${index}`}
                      onPress={() => handleSelectOrganization(item)}
                      style={{ padding: 12, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: palette.gray[10] }}
                      testID={tid('organization-item', index)}
                    >
                      <ThemedText type='bodyRegular'>{item.name}</ThemedText>
                    </BaseButton>
                  ))}
                </ScrollView>
              </View>
            )}

            {showSuggestions && suggestions.length === 0 && searchTerm.trim() !== '' && !organizationsLoading && (
              <View style={dropdownStyle}>
                <View className='p-4 items-center'>
                  <ThemedText type='bodySmall' color='subtle'>
                    {t('Nothing found.')}
                  </ThemedText>
                </View>
              </View>
            )}
          </View>

          <View className='mt-6 gap-4'>
            <View>
              <BloomButton
                text={t('Continue')}
                onPress={handleSubmit}
                loading={loading}
                disabled={!selectedOrg}
                testID={tid('continue-button')}
              />
            </View>

            {Platform.OS === 'web' && zendeskSsoUrl && (
              <View>
                <View className='flex-row items-center mt-2 mb-6'>
                  <View className='flex-1 border-t border-login-gray-10' />
                  <ThemedText type='overline' color='subtle' className='mx-3' style={{ fontSize: 11 }}>
                    {t('or')}
                  </ThemedText>
                  <View className='flex-1 border-t border-login-gray-10' />
                </View>

                <ThemedText type='bodySmallBold' className='mb-1'>
                  {t('Work at Vida?')}
                </ThemedText>
                <ThemedText type='bodySmall' color='subtle' className='mb-4'>
                  {t('Sign in with Okta to continue to Zendesk.')}
                </ThemedText>
                <BloomButton
                  text={t('Continue with Okta')}
                  onPress={handleZendeskOktaSignIn}
                  hierarchy='secondary'
                  testID={tid('zendesk-okta-button')}
                />
              </View>
            )}

            <AuthFooter
              variant='secondary'
              onBackToLogin={handleBackToLogin}
              testID={tid('footer')}
            />
          </View>
        </View>
      </View>
    </AuthLayout>
  );
}
